import { useEffect, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { useLantern, useSession } from "../hooks";
import { navigate } from "../router";
import { localInput } from "../reminders";
import { Avatar, Field, Form, Sheet, useMenu, usePlace } from "./ui";
import { locale, t, tx } from "../i18n";
import { EmojiPicker } from "./EmojiPicker";
import { glyph } from "../emoji";
import { setPhoto } from "../avatar";
import { IconBell, IconCamera, IconClose, IconGear, IconMoon, IconPerson, IconSignOut, IconSun } from "./icons";

// AccountMenu is the button at the bottom of the sidebar, as in Slack: your
// status, being away, pausing notifications, your profile, preferences,
// account settings and signing out.
export function AccountMenu({ onSignOut }: { onSignOut: () => void }) {
  const session = useSession();
  const state = useLantern();
  const me = state.me;
  const [open, setOpen] = useState(false);
  const [sub, setSub] = useState(false);
  const [dialog, setDialog] = useState<"status" | "pause" | null>(null);
  const [error, setError] = useState("");
  const box = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  // Outside the menu, which closes while the file picker is open.
  const photo = useRef<HTMLInputElement>(null);
  // The menu and dialogs are drawn at the page's top level, so the
  // sidebar's scrolling neither clips nor styles them; the menu sits just
  // above the button.
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  useMenu(
    menu,
    () => {
      setOpen(false);
      setSub(false);
    },
    { keep: box, open: open && !!anchor },
  );
  const place = usePlace(menu, anchor ?? { top: 0, bottom: 0, left: 0, right: 0 }, { side: "above" });

  // Quiet hours begin and end on their own: look again each minute.
  const [, tick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => tick((n) => n + 1), 60_000);
    return () => clearInterval(timer);
  }, []);
  if (!me) return null;

  const presence = state.presence.get(me.id) ?? (me.away ? "away" : "active");
  const status = session.statusOf(me.id);
  const pausedFor = session.pausedFor();
  const paused = pausedFor !== null;
  const act = async (fn: () => Promise<unknown>) => {
    setError("");
    try {
      await fn();
      setOpen(false);
      setSub(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };
  const pauseFor = (until: Date) => act(() => session.pause(until));
  const show = (d: typeof dialog) => {
    setOpen(false);
    setSub(false);
    setDialog(d);
  };

  return (
    <div className="account" ref={box}>
      <input
        ref={photo}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) setPhoto(session, file).catch((err) => session.popUp({ id: "photo", title: t("Could not set your photo"), body: err instanceof Error ? err.message : String(err) }));
        }}
      />
      {open &&
        anchor &&
        createPortal(
        <div
          className="account-menu"
          role="menu"
          aria-label={t("Account")}
          ref={menu}
          style={place}
        >
          <div className="account-head">
            <Avatar id={me.id} name={me.display_name || me.handle} size={44} presence={presence} />
            <div>
              <strong>{me.display_name || me.handle}</strong>
              <span className={`presence-text ${presence}`}>{presence === "away" ? t("Away") : t("Active")}</span>
            </div>
          </div>
          <button className="status-button" role="menuitem" onClick={() => show("status")}>
            {status ? (
              <>
                <span className="status-emoji">{status.emoji || "💬"}</span> {status.text || t("Status set")}
              </>
            ) : (
              <>
                <span className="status-emoji">🙂</span> {t("Update your status")}
              </>
            )}
          </button>
          <button
            role="menuitem"
            onClick={() => {
              setOpen(false);
              photo.current?.click();
            }}
          >
            <IconCamera size={16} />
            {me.avatar_at ? t("Change profile photo") : t("Add a profile photo")}
          </button>
          {status && (
            <button role="menuitem" className="quiet" onClick={() => act(() => session.setOwn(() => session.api.setStatus("", "", null)))}>
              <IconClose size={16} />
              {t("Clear status")}
            </button>
          )}
          <button role="menuitem" onClick={() => act(() => session.setOwn(() => session.api.setAway(!me.away)))}>
            {me.away ? <IconSun size={16} /> : <IconMoon size={16} />}
            <span>{tx("Set yourself as {presence}", { presence: <strong>{me.away ? t("active") : t("away")}</strong> })}</span>
          </button>
          <div className="has-sub" onMouseEnter={() => setSub(true)} onMouseLeave={() => setSub(false)}>
            <button role="menuitem" aria-haspopup="menu" aria-expanded={sub} onClick={() => setSub(true)}>
              <IconBell size={16} off={!!pausedFor} />
              {t("Notifications")}
              <span className="aside">
                {pausedFor
                  ? pausedFor.quiet
                    ? t("Quiet hours until {time}", { time: until(pausedFor.until) })
                    : t("Paused until {time}", { time: until(pausedFor.until) })
                  : t("On")}{" "}
                ›
              </span>
            </button>
            {sub && (
              <div className="account-submenu" role="menu" aria-label={t("Pause notifications")}>
                {pausedFor && !pausedFor.quiet ? (
                  <button role="menuitem" onClick={() => act(() => session.pause(null))}>
                    {t("Resume notifications")}
                  </button>
                ) : pausedFor?.quiet ? (
                  <p className="label">{t("Quiet hours until {time}. Pause longer for…", { time: until(pausedFor.until) })}</p>
                ) : (
                  <p className="label">{t("Pause notifications for…")}</p>
                )}
                <button role="menuitem" onClick={() => pauseFor(minutesFromNow(30))}>
                  {t("30 minutes")}
                </button>
                <button role="menuitem" onClick={() => pauseFor(minutesFromNow(60))}>
                  {t("1 hour")}
                </button>
                <button role="menuitem" onClick={() => pauseFor(minutesFromNow(120))}>
                  {t("2 hours")}
                </button>
                <button role="menuitem" onClick={() => pauseFor(tomorrowAt(9))}>
                  {t("Until tomorrow")}
                </button>
                <button role="menuitem" onClick={() => pauseFor(nextMondayAt(9))}>
                  {t("Until next week")}
                </button>
                <button role="menuitem" onClick={() => show("pause")}>
                  {t("Custom…")}
                </button>
                <p className="hint">{t("Paused, DMs and mentions still show as counts, without pop-ups, sounds or desktop notifications.")}</p>
                <button
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    setSub(false);
                    navigate("/settings/notifications");
                  }}
                >
                  {t("Notification settings…")}
                </button>
              </div>
            )}
          </div>
          <hr />
          <button
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate("/settings/profile");
            }}
          >
            <IconPerson size={16} />
            {t("Profile")}
          </button>
          <button
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate("/settings/preferences");
            }}
          >
            <IconGear size={16} />
            {t("Settings")}
          </button>
          <hr />
          <button role="menuitem" onClick={onSignOut}>
            <IconSignOut size={16} />
            {t("Sign out of {workspace}", { workspace: state.workspace || "Lantern" })}
          </button>
          {error && <p className="error">{error}</p>}
        </div>,
          document.body,
        )}
      <button
        className="account-button"
        aria-label={t("Account: {name}, {presence}", { name: me.display_name || me.handle, presence: presence === "away" ? t("away") : t("active") })}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          setAnchor(e.currentTarget.getBoundingClientRect());
          setOpen(!open);
          setSub(false);
        }}
      >
        <Avatar id={me.id} name={me.display_name || me.handle} size={32} presence={presence} />
        <span className="account-name">{me.display_name || me.handle}</span>
        {status?.emoji && <span className="status-emoji" title={status.text}>{status.emoji}</span>}
        {paused && (
          <span className="paused" title={pausedFor.quiet ? t("Quiet hours") : t("Notifications paused")}>
            🌙
          </span>
        )}
      </button>
      {dialog === "status" && <StatusDialog onClose={() => setDialog(null)} />}
      {dialog === "pause" && <PauseDialog onClose={() => setDialog(null)} />}
    </div>
  );
}

// Avatar is a person's initials on a colour of their own, with a presence dot.

const presets = (): { emoji: string; text: string; clear: string }[] => [
  { emoji: "📅", text: t("In a meeting"), clear: "1h" },
  { emoji: "🚌", text: t("Commuting"), clear: "30m" },
  { emoji: "🤒", text: t("Out sick"), clear: "today" },
  { emoji: "🌴", text: t("Vacationing"), clear: "never" },
  { emoji: "🏡", text: t("Working remotely"), clear: "today" },
];
const clears = (): [string, string][] => [
  ["never", t("Don't clear")],
  ["30m", t("30 minutes")],
  ["1h", t("1 hour")],
  ["4h", t("4 hours")],
  ["today", t("Today")],
  ["week", t("This week")],
];

function clearAt(choice: string): Date | null {
  switch (choice) {
    case "30m":
      return minutesFromNow(30);
    case "1h":
      return minutesFromNow(60);
    case "4h":
      return minutesFromNow(240);
    case "today":
      return tomorrowAt(0);
    case "week":
      return nextMondayAt(0);
  }
  return null;
}

export function StatusDialog({ onClose }: { onClose: () => void }) {
  const session = useSession();
  const me = session.store.getSnapshot().me!;
  const current = session.statusOf(me.id);
  const [emoji, setEmoji] = useState(current?.emoji || "💬");
  const [picking, setPicking] = useState<DOMRect | null>(null);
  const [text, setText] = useState(current?.text ?? "");
  const [clear, setClear] = useState("today");
  const [error, setError] = useState("");
  async function save(e?: FormEvent, empty = false) {
    e?.preventDefault();
    setError("");
    try {
      const at = empty ? null : clearAt(clear);
      await session.setOwn(() => session.api.setStatus(empty ? "" : emoji, empty ? "" : text, at ? at.toISOString() : null));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }
  return (
    <Sheet title={t("Set a status")} onClose={onClose}>
      <Form onSubmit={save} className="status-form">
        <div className="status-input">
          <button type="button" className="status-emoji-button" aria-label={t("Emoji: {emoji}", { emoji })} title={t("Choose an emoji")} onClick={(e) => setPicking(e.currentTarget.getBoundingClientRect())}>
            {emoji}
          </button>
          {picking && (
            <EmojiPicker
              anchor={picking}
              onClose={() => setPicking(null)}
              onPick={(name) => {
                setEmoji(glyph(name));
                setPicking(null);
              }}
            />
          )}
          <input aria-label={t("What's your status?")} placeholder={t("What's your status?")} value={text} maxLength={100} onChange={(e) => setText(e.target.value)} />
        </div>
        <ul className="presets">
          {presets().map((p) => (
            <li key={p.text}>
              <button
                type="button"
                onClick={() => {
                  setEmoji(p.emoji);
                  setText(p.text);
                  setClear(p.clear);
                }}
              >
                {p.emoji} {p.text} <span>· {clears().find((c) => c[0] === p.clear)?.[1]}</span>
              </button>
            </li>
          ))}
        </ul>
        <label>
          {t("Clear after")}
          <select value={clear} onChange={(e) => setClear(e.target.value)}>
            {clears().map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
        {error && <p className="error">{error}</p>}
        <div className="actions">
          {current && (
            <button type="button" className="secondary" onClick={() => void save(undefined, true)}>
              {t("Clear status")}
            </button>
          )}
          <button type="submit" disabled={!text.trim()}>
            {t("Save")}
          </button>
        </div>
      </Form>
    </Sheet>
  );
}

function PauseDialog({ onClose }: { onClose: () => void }) {
  const session = useSession();
  const [at, setAt] = useState(() => localInput(minutesFromNow(90)));
  const [error, setError] = useState("");
  async function save(e: FormEvent) {
    e.preventDefault();
    setError("");
    try {
      await session.pause(new Date(at));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }
  return (
    <Sheet title={t("Pause notifications until")} onClose={onClose}>
      <Form onSubmit={save}>
        <Field label={t("Until")}>
          <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} required />
        </Field>
        {error && <p className="error">{error}</p>}
        <div className="actions">
          <button type="submit">{t("Pause")}</button>
        </div>
      </Form>
    </Sheet>
  );
}

function minutesFromNow(n: number) {
  return new Date(Date.now() + n * 60_000);
}

function tomorrowAt(hour: number) {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(hour, 0, 0, 0);
  return d;
}

function nextMondayAt(hour: number) {
  const d = new Date();
  d.setDate(d.getDate() + (((8 - d.getDay()) % 7) || 7));
  d.setHours(hour, 0, 0, 0);
  return d;
}

// until is a short time: "3:30 PM" today, else the day too.
function until(d: Date) {
  const today = new Date().toDateString() === d.toDateString();
  return today
    ? d.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" })
    : d.toLocaleString(locale, { weekday: "short", hour: "numeric", minute: "2-digit" });
}
